import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  PaEmptyState,
  PaSkeleton,
  PaUsageScoreRing,
} from "components/platform-admin";
import { platformRoleAccess } from "helpers/RolesPermission";
import {
  fetchPlatformTenantUsage,
  refreshPlatformTenantUsage,
  savePlatformUsageComment,
} from "store/services/platformAdmin.services";
import { formatUsageTimestamp } from "./usageOverview.utils";

const TenantUsageTab = ({ tenantId }) => {
  const canViewUsage = platformRoleAccess("platform.usage.view");
  const canComment = platformRoleAccess("platform.usage.comment");
  const canRefresh = platformRoleAccess("platform.usage.refresh");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [draftComments, setDraftComments] = useState({});
  const [savingModule, setSavingModule] = useState(null);

  const loadUsage = useCallback(async () => {
    if (!canViewUsage || !tenantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchPlatformTenantUsage(tenantId);
      setUsage(data);
      const nextDrafts = {};
      (data?.modules || []).forEach((module) => {
        nextDrafts[module.key] = module.comment || "";
      });
      setDraftComments(nextDrafts);
    } catch (err) {
      setUsage(null);
      setError(err?.message || "Failed to load tenant usage");
    } finally {
      setLoading(false);
    }
  }, [canViewUsage, tenantId]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshPlatformTenantUsage(tenantId);
      toast.success("Usage snapshot refreshed");
      await loadUsage();
    } catch (err) {
      toast.error(err?.message || "Failed to refresh usage");
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaveComment = async (moduleKey) => {
    setSavingModule(moduleKey);
    try {
      await savePlatformUsageComment(tenantId, moduleKey, draftComments[moduleKey] || "");
      toast.success("Comment saved");
      await loadUsage();
    } catch (err) {
      toast.error(err?.message || "Failed to save comment");
    } finally {
      setSavingModule(null);
    }
  };

  if (!canViewUsage) {
    return (
      <PaEmptyState
        title="Access restricted"
        message="You don't have access to usage analytics"
        description="You need platform.usage.view."
      />
    );
  }

  if (loading) {
    return <PaSkeleton variant="card" />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-gray-500">
          <p>Module scores use equal-weight signals over the last 30 days.</p>
          {usage?.computed_at ? (
            <p className="mt-1">Snapshot as of {formatUsageTimestamp(usage.computed_at)}</p>
          ) : (
            <p className="mt-1 text-amber-700">No saved snapshot — showing live computed scores.</p>
          )}
        </div>
        {canRefresh ? (
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {refreshing ? "Refreshing…" : "Refresh snapshot"}
          </button>
        ) : null}
      </div>

      {(usage?.modules || []).map((module) => (
        <section key={module.key} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">{module.label}</h3>
              <p className="mt-1 text-sm text-gray-500">{module.signals?.length || 0} tracked signals</p>
            </div>
            <PaUsageScoreRing score={module.score} />
          </div>

          {module.signals?.length ? (
            <ul className="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-100">
              {module.signals.map((signal) => (
                <li key={signal.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                  <span className="text-gray-700">{signal.label}</span>
                  <span className="capitalize text-gray-500">
                    {signal.state?.replace(/_/g, " ")} · {signal.count ?? 0}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          {canComment ? (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Platform comment</label>
              <textarea
                rows={3}
                value={draftComments[module.key] ?? ""}
                onChange={(event) =>
                  setDraftComments((current) => ({
                    ...current,
                    [module.key]: event.target.value,
                  }))
                }
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => handleSaveComment(module.key)}
                disabled={savingModule === module.key}
                className="mt-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {savingModule === module.key ? "Saving…" : "Save comment"}
              </button>
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
};

export default TenantUsageTab;
